import { useEffect, useState } from 'react';
import api from '../services/api';
import './FlowkitDashboardTab.css';

export interface FlowkitDashboardTabProps {
  /** Existing native Flowkit project ID, not a newly generated provider project. */
  projectId?: string;
}

export function flowkitDashboardSrc(projectId?: string): string | null {
  if (projectId === undefined) return '/flowkit-dashboard/';
  if (!/^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(projectId)) return null;
  return `/flowkit-dashboard/projects/${projectId}?tab=pipeline`;
}

type SessionResult = { src: string; attempt: number; state: 'ready' | 'failed' };

export default function FlowkitDashboardTab({ projectId }: FlowkitDashboardTabProps) {
  const src = flowkitDashboardSrc(projectId);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<SessionResult | null>(null);
  const state = result?.src === src && result?.attempt === attempt ? result.state : 'loading';

  useEffect(() => {
    if (!src) return;
    const controller = new AbortController();
    // The existing helper supplies the current Studio bearer. The idempotent
    // endpoint only sets the HttpOnly fallback when it needs to be bootstrapped.
    // https://react.dev/reference/react/useEffect#fetching-data-with-effects
    void api.post('/flowkit-dashboard/session', undefined, {
      signal: controller.signal, timeout: 10_000, withCredentials: true,
    }).then(response => {
      if (controller.signal.aborted) return;
      if (response.status !== 204) throw new Error('Unexpected session response');
      setResult({ src, attempt, state: 'ready' });
    }).catch(() => {
      if (!controller.signal.aborted) setResult({ src, attempt, state: 'failed' });
    });
    return () => controller.abort();
  }, [src, attempt]);

  return (
    <section className="flowkit-dashboard-tab" aria-label="Flowkit dashboard">
      <header className="flowkit-dashboard-tab__header">
        <h2>Flowkit Dashboard</h2>
        <span>Native operations console</span>
      </header>
      {!src ? (
        <div className="flowkit-dashboard-tab__status" role="alert">
          Select a valid saved Flowkit project to open its pipeline.
        </div>
      ) : state === 'loading' ? (
        <div className="flowkit-dashboard-tab__status" role="status" aria-live="polite">
          Preparing the authenticated dashboard…
        </div>
      ) : state === 'failed' ? (
        <div className="flowkit-dashboard-tab__status" role="alert">
          <p>The dashboard session could not be prepared. Check the Studio session and retry.</p>
          <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
        </div>
      ) : (
        <iframe
          key={src}
          src={src}
          title="Flowkit Dashboard"
          className="flowkit-dashboard-tab__frame"
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
        />
      )}
    </section>
  );
}